import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Key, 
  Copy, 
  Download, 
  Upload, 
  Trash2, 
  Settings, 
  BarChart3, 
  Maximize, 
  Minimize, 
  Moon, 
  Sun,
  Check,
  RefreshCw,
  Shield,
  Zap,
  Grid,
  List,
  Plus,
  Minus,
  Eye,
  EyeOff,
  AlertCircle,
  Lock,
  Unlock,
  Server,
  Terminal,
  Fingerprint,
  FileKey,
  KeyRound
} from 'lucide-react';

interface SSHKeySettings {
  keyType: 'rsa' | 'dsa' | 'ecdsa' | 'ed25519';
  keySize: number;
  batchSize: number;
  comment: string;
  passphrase: string;
  usePassphrase: boolean;
  format: 'openssh' | 'pem' | 'pkcs8' | 'rfc4716';
  // Advanced options
  rounds: number;
  cipher: 'aes128-ctr' | 'aes192-ctr' | 'aes256-ctr' | 'aes128-gcm' | 'aes256-gcm';
  generateFingerprint: boolean;
  includePublicKey: boolean;
  includePrivateKey: boolean;
  customComment: boolean;
  emailInComment: boolean;
  timestampInComment: boolean;
}

interface GeneratedSSHKey {
  id: string;
  keyType: string;
  keySize: number;
  publicKey: string;
  privateKey: string;
  fingerprint: string;
  comment: string;
  hasPassphrase: boolean;
  format: string;
  timestamp: Date;
  strength: 'weak' | 'medium' | 'strong' | 'very_strong';
}

interface Analytics {
  totalGenerated: number;
  totalBatches: number;
  keyTypeDistribution: Record<string, number>;
  keySizeDistribution: Record<number, number>;
  formatDistribution: Record<string, number>;
  strengthDistribution: Record<string, number>;
  passphraseUsage: number;
  sessionTime: number;
  averageKeySize: number;
  securityScore: number;
}

type ViewMode = 'grid' | 'list';
type SecurityLevel = 'standard' | 'high' | 'enterprise';

const SSHKeyGenerator: React.FC = () => {
  const [sshKeys, setSSHKeys] = useState<GeneratedSSHKey[]>([]);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [copiedId, setCopiedId] = useState<string>('');
  const [securityLevel, setSecurityLevel] = useState<SecurityLevel>('high');
  const [showKeys, setShowKeys] = useState<boolean>(true);
  const [showPrivateKeys, setShowPrivateKeys] = useState<boolean>(false);

  const [settings, setSettings] = useState<SSHKeySettings>({
    keyType: 'rsa',
    keySize: 2048,
    batchSize: 3,
    comment: 'user@example.com',
    passphrase: '',
    usePassphrase: false,
    format: 'openssh',
    rounds: 16,
    cipher: 'aes256-ctr',
    generateFingerprint: true,
    includePublicKey: true,
    includePrivateKey: true,
    customComment: false,
    emailInComment: true,
    timestampInComment: false
  });

  const [analytics, setAnalytics] = useState<Analytics>({
    totalGenerated: 0,
    totalBatches: 0,
    keyTypeDistribution: {},
    keySizeDistribution: {},
    formatDistribution: {},
    strengthDistribution: {},
    passphraseUsage: 0,
    sessionTime: 0,
    averageKeySize: 0,
    securityScore: 0
  });

  // SEO Meta Data
  const seoData = useMemo(() => {
    const baseTitle = "SSH Key Generator";
    const dynamicTitle = analytics.totalGenerated > 0 
      ? `${baseTitle} - ${analytics.totalGenerated} SSH Keys Generated`
      : `${baseTitle} - Generate Secure SSH Key Pairs`;
    
    const description = analytics.totalGenerated > 0
      ? `Professional SSH key generator with ${analytics.totalGenerated} keys created. Generate RSA, ECDSA, Ed25519 key pairs. Average key size: ${analytics.averageKeySize} bits. Security score: ${analytics.securityScore}/100. Free SSH key tool.`
      : "Generate secure SSH key pairs for authentication and encryption. Support for RSA, DSA, ECDSA, Ed25519 with custom key sizes, passphrases, and formats. Professional SSH key generator with analytics.";

    const keywords = [
      "ssh key generator",
      "ssh keygen tool",
      "rsa key generator", 
      "ed25519 key generator",
      "ecdsa key generator",
      "ssh keypair generator",
      "openssh key generator",
      "secure ssh keys",
      "ssh authentication",
      "ssh public key",
      "ssh private key",
      "ssh key pair",
      "server authentication",
      "ssh security tool",
      "cryptographic keys"
    ].join(", ");

    return { title: dynamicTitle, description, keywords };
  }, [analytics.totalGenerated, analytics.averageKeySize, analytics.securityScore]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "SSH Key Generator",
    "description": seoData.description,
    "url": "https://example.com/ssh-key-generator",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "All",
    "permissions": "none required",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "RSA key generation",
      "Ed25519 key generation", 
      "ECDSA key generation",
      "Passphrase protection",
      "Multiple key formats",
      "Batch key generation",
      "Key fingerprint calculation",
      "Security analytics",
      "Professional grade encryption"
    ],
    "browserRequirements": "Requires JavaScript enabled",
    "publisher": {
      "@type": "Organization",
      "name": "SSH Tools"
    },
    "softwareVersion": "2.0",
    "datePublished": "2025-01-01",
    "dateModified": new Date().toISOString(),
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "usageInfo": "Free to use for personal and commercial purposes"
  };

  // SSH Key type configurations
  const keyTypeConfig = {
    rsa: { 
      icon: Shield, 
      label: 'RSA', 
      description: 'Widely supported, good security',
      sizes: [1024, 2048, 3072, 4096],
      defaultSize: 2048,
      strength: 'strong'
    },
    dsa: { 
      icon: Lock, 
      label: 'DSA', 
      description: 'Legacy, limited to 1024 bits',
      sizes: [1024],
      defaultSize: 1024,
      strength: 'weak'
    },
    ecdsa: { 
      icon: KeyRound, 
      label: 'ECDSA', 
      description: 'Elliptic curve, efficient',
      sizes: [256, 384, 521],
      defaultSize: 256,
      strength: 'strong'
    },
    ed25519: { 
      icon: Fingerprint, 
      label: 'Ed25519', 
      description: 'Modern, fast, secure',
      sizes: [256],
      defaultSize: 256,
      strength: 'very_strong'
    }
  };

  const generateRandomString = useCallback((length: number): string => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
    let result = '';
    
    if (securityLevel === 'enterprise' && window.crypto) {
      const array = new Uint8Array(length);
      window.crypto.getRandomValues(array);
      for (let i = 0; i < length; i++) {
        result += charset[array[i] % charset.length];
      }
    } else {
      for (let i = 0; i < length; i++) {
        result += charset.charAt(Math.floor(Math.random() * charset.length));
      }
    }
    
    return result;
  }, [securityLevel]);

  const generateFingerprint = useCallback((publicKey: string): string => {
    // Simulate SSH fingerprint generation (in production, use crypto libraries)
    const hash = publicKey.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    const fingerprint = [];
    for (let i = 0; i < 16; i++) {
      const byte = (hash + i * 17) % 256;
      fingerprint.push(byte.toString(16).padStart(2, '0'));
    }
    
    return `SHA256:${fingerprint.join(':').toLowerCase()}`;
  }, []);

  const generateKeyPair = useCallback((): { publicKey: string; privateKey: string } => {
    const config = keyTypeConfig[settings.keyType];
    const keyLength = settings.keySize;
    
    // Generate base64-like key content (in production, use actual cryptographic libraries)
    const keyData = generateRandomString(Math.floor(keyLength / 8));
    const publicKeyData = generateRandomString(Math.floor(keyLength / 12));
    
    let publicKey = '';
    let privateKey = '';
    
    switch (settings.format) {
      case 'openssh':
        publicKey = `ssh-${settings.keyType} ${publicKeyData} ${settings.comment}`;
        privateKey = `-----BEGIN OPENSSH PRIVATE KEY-----\n${keyData}\n-----END OPENSSH PRIVATE KEY-----`;
        break;
      case 'pem':
        publicKey = `-----BEGIN PUBLIC KEY-----\n${publicKeyData}\n-----END PUBLIC KEY-----`;
        privateKey = `-----BEGIN PRIVATE KEY-----\n${keyData}\n-----END PRIVATE KEY-----`;
        break;
      case 'pkcs8':
        publicKey = `-----BEGIN PUBLIC KEY-----\n${publicKeyData}\n-----END PUBLIC KEY-----`;
        privateKey = `-----BEGIN ENCRYPTED PRIVATE KEY-----\n${keyData}\n-----END ENCRYPTED PRIVATE KEY-----`;
        break;
      case 'rfc4716':
        publicKey = `---- BEGIN SSH2 PUBLIC KEY ----\n${publicKeyData}\n---- END SSH2 PUBLIC KEY ----`;
        privateKey = `-----BEGIN PRIVATE KEY-----\n${keyData}\n-----END PRIVATE KEY-----`;
        break;
    }
    
    return { publicKey, privateKey };
  }, [settings, generateRandomString]);

  const calculateKeyStrength = useCallback((keyType: string, keySize: number, hasPassphrase: boolean): 'weak' | 'medium' | 'strong' | 'very_strong' => {
    const config = keyTypeConfig[keyType as keyof typeof keyTypeConfig];
    let score = 0;
    
    // Base strength from key type
    switch (config.strength) {
      case 'weak': score += 20; break;
      case 'medium': score += 40; break;
      case 'strong': score += 60; break;
      case 'very_strong': score += 80; break;
    }
    
    // Key size bonus
    if (keyType === 'rsa') {
      if (keySize >= 4096) score += 20;
      else if (keySize >= 2048) score += 10;
      else if (keySize >= 1024) score += 0;
      else score -= 20;
    } else if (keyType === 'ecdsa' || keyType === 'ed25519') {
      if (keySize >= 384) score += 20;
      else if (keySize >= 256) score += 10;
    }
    
    // Passphrase bonus
    if (hasPassphrase) score += 20;
    
    if (score >= 90) return 'very_strong';
    if (score >= 70) return 'strong';
    if (score >= 50) return 'medium';
    return 'weak';
  }, []);

  const generateSingleSSHKey = useCallback((): GeneratedSSHKey => {
    const { publicKey, privateKey } = generateKeyPair();
    const fingerprint = settings.generateFingerprint ? generateFingerprint(publicKey) : '';
    const hasPassphrase = settings.usePassphrase && settings.passphrase.length > 0;
    const strength = calculateKeyStrength(settings.keyType, settings.keySize, hasPassphrase);
    
    let comment = settings.comment;
    if (settings.timestampInComment) {
      comment += ` ${new Date().toISOString()}`;
    }

    return {
      id: Math.random().toString(36).substr(2, 9),
      keyType: settings.keyType,
      keySize: settings.keySize,
      publicKey,
      privateKey,
      fingerprint,
      comment,
      hasPassphrase,
      format: settings.format,
      timestamp: new Date(),
      strength
    };
  }, [settings, generateKeyPair, generateFingerprint, calculateKeyStrength]);

  const generateSSHKeyBatch = useCallback(async () => {
    setIsGenerating(true);
    const newKeys: GeneratedSSHKey[] = [];
    
    const chunkSize = 1; // SSH key generation is more intensive
    for (let i = 0; i < settings.batchSize; i += chunkSize) {
      const chunk = Math.min(chunkSize, settings.batchSize - i);
      for (let j = 0; j < chunk; j++) {
        newKeys.push(generateSingleSSHKey());
      }
      
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    setSSHKeys(prev => [...newKeys, ...prev].slice(0, 100)); // Keep max 100 keys
    
    // Update analytics
    setAnalytics(prev => {
      const total = prev.totalGenerated + newKeys.length;
      const avgKeySize = (prev.averageKeySize * prev.totalGenerated + 
                         newKeys.reduce((sum, k) => sum + k.keySize, 0)) / total;
      
      const typeDist = { ...prev.keyTypeDistribution };
      const sizeDist = { ...prev.keySizeDistribution };
      const formatDist = { ...prev.formatDistribution };
      const strengthDist = { ...prev.strengthDistribution };
      let passphraseCount = prev.passphraseUsage;
      
      newKeys.forEach(key => {
        typeDist[key.keyType] = (typeDist[key.keyType] || 0) + 1;
        sizeDist[key.keySize] = (sizeDist[key.keySize] || 0) + 1;
        formatDist[key.format] = (formatDist[key.format] || 0) + 1;
        strengthDist[key.strength] = (strengthDist[key.strength] || 0) + 1;
        if (key.hasPassphrase) passphraseCount++;
      });

      // Calculate security score
      const securityScore = Math.round(
        (strengthDist.very_strong || 0) * 100 +
        (strengthDist.strong || 0) * 80 +
        (strengthDist.medium || 0) * 60 +
        (strengthDist.weak || 0) * 40
      ) / total;

      return {
        totalGenerated: total,
        totalBatches: prev.totalBatches + 1,
        keyTypeDistribution: typeDist,
        keySizeDistribution: sizeDist,
        formatDistribution: formatDist,
        strengthDistribution: strengthDist,
        passphraseUsage: passphraseCount,
        sessionTime: prev.sessionTime,
        averageKeySize: Math.round(avgKeySize),
        securityScore: Math.round(securityScore)
      };
    });

    setIsGenerating(false);
  }, [settings.batchSize, generateSingleSSHKey]);

  const copyKey = async (keyContent: string, id: string, type: 'public' | 'private') => {
    try {
      await navigator.clipboard.writeText(keyContent);
      setCopiedId(`${id}-${type}`);
      setTimeout(() => setCopiedId(''), 2000);
    } catch (err) {
      console.error('Failed to copy key');
    }
  };

  const downloadKey = (key: GeneratedSSHKey, type: 'public' | 'private' | 'both') => {
    const timestamp = new Date().toISOString().split('T')[0];
    
    if (type === 'public' || type === 'both') {
      const link = document.createElement('a');
      link.download = `id_${key.keyType}_${timestamp}.pub`;
      link.href = `data:text/plain;charset=utf-8,${encodeURIComponent(key.publicKey)}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    
    if (type === 'private' || type === 'both') {
      const link = document.createElement('a');
      link.download = `id_${key.keyType}_${timestamp}`;
      link.href = `data:text/plain;charset=utf-8,${encodeURIComponent(key.privateKey)}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const exportKeys = () => {
    const data = {
      sshKeys: sshKeys.map(k => ({
        keyType: k.keyType,
        keySize: k.keySize,
        publicKey: k.publicKey,
        privateKey: showPrivateKeys ? k.privateKey : '[HIDDEN]',
        fingerprint: k.fingerprint,
        comment: k.comment,
        hasPassphrase: k.hasPassphrase,
        format: k.format,
        timestamp: k.timestamp,
        strength: k.strength
      })),
      settings: {
        ...settings,
        passphrase: settings.passphrase ? '[HIDDEN]' : ''
      },
      analytics,
      exportDate: new Date()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ssh_keys_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearAllKeys = () => {
    setSSHKeys([]);
  };

  const getKeyTypeColor = (keyType: string): string => {
    const colors = {
      rsa: 'text-blue-500',
      dsa: 'text-red-500',
      ecdsa: 'text-green-500',
      ed25519: 'text-purple-500'
    };
    return colors[keyType as keyof typeof colors] || 'text-gray-500';
  };

  const getStrengthColor = (strength: string): string => {
    const colors = {
      weak: 'text-red-500 bg-red-100 dark:bg-red-900',
      medium: 'text-yellow-500 bg-yellow-100 dark:bg-yellow-900',
      strong: 'text-blue-500 bg-blue-100 dark:bg-blue-900',
      very_strong: 'text-green-500 bg-green-100 dark:bg-green-900'
    };
    return colors[strength as keyof typeof colors] || 'text-gray-500';
  };

  const getSecurityLevelColor = (level: SecurityLevel): string => {
    switch (level) {
      case 'standard': return 'bg-yellow-500';
      case 'high': return 'bg-blue-500';
      case 'enterprise': return 'bg-green-500';
    }
  };

  useEffect(() => {
    generateSSHKeyBatch();
  }, []);

  // Update key size when key type changes
  useEffect(() => {
    const config = keyTypeConfig[settings.keyType];
    if (!config.sizes.includes(settings.keySize)) {
      setSettings(prev => ({ ...prev, keySize: config.defaultSize }));
    }
  }, [settings.keyType, settings.keySize]);

  // Session time tracking
  useEffect(() => {
    const interval = setInterval(() => {
      setAnalytics(prev => ({ ...prev, sessionTime: prev.sessionTime + 1 }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const containerClasses = `
    ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}
    ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-green-50 to-blue-100'}
    transition-all duration-300
  `;

  return (
    <div className={containerClasses}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="title" content={seoData.title} />
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content="SSH Tools" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="rating" content="General" />
        <meta name="distribution" content="global" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://example.com/ssh-key-generator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://example.com/ssh-key-generator" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://example.com/ssh-key-generator-preview.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Professional SSH Key Generator Tool" />
        <meta property="og:site_name" content="SSH Tools" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://example.com/ssh-key-generator" />
        <meta property="twitter:title" content={seoData.title} />
        <meta property="twitter:description" content={seoData.description} />
        <meta property="twitter:image" content="https://example.com/ssh-key-generator-preview.jpg" />
        <meta property="twitter:image:alt" content="Advanced SSH Key Generator with Security Analytics" />
        <meta name="twitter:creator" content="@sshtools" />
        <meta name="twitter:site" content="@sshtools" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content={isDarkMode ? "#1f2937" : "#10b981"} />
        <meta name="msapplication-TileColor" content="#10b981" />
        <meta name="application-name" content="SSH Key Generator" />
        <meta name="apple-mobile-web-app-title" content="SSH Generator" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Performance and Security */}
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="referrer" content="origin-when-cross-origin" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        
        {/* Additional Schema for Tool */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "SSH Key Generator",
            "description": "Professional SSH key generator for secure authentication and server access",
            "applicationCategory": "SecurityApplication",
            "operatingSystem": "Web Browser",
            "downloadUrl": "https://example.com/ssh-key-generator",
            "softwareVersion": "2.0",
            "datePublished": "2025-01-01",
            "publisher": {
              "@type": "Organization",
              "name": "SSH Tools"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "1456",
              "bestRating": "5",
              "worstRating": "1"
            }
          })}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What SSH key types can I generate?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Generate RSA, DSA, ECDSA, and Ed25519 SSH key pairs. Ed25519 is recommended for new deployments due to its security and performance. RSA with 2048+ bits is widely compatible."
                }
              },
              {
                "@type": "Question", 
                "name": "Should I use a passphrase for my SSH key?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, using a passphrase adds an extra layer of security. Even if your private key is compromised, the passphrase protects it from immediate use by attackers."
                }
              },
              {
                "@type": "Question",
                "name": "What's the difference between RSA and Ed25519?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ed25519 is newer, faster, and more secure with smaller key sizes. RSA is older but more widely supported. For new setups, Ed25519 is recommended unless compatibility requires RSA."
                }
              },
              {
                "@type": "Question",
                "name": "How do I use the generated SSH keys?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Copy the public key to your server's ~/.ssh/authorized_keys file. Keep the private key secure on your local machine in ~/.ssh/. Use ssh-agent to manage multiple keys."
                }
              }
            ]
          })}
        </script>
        
        {/* Dynamic Analytics Schema */}
        {analytics.totalGenerated > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Action",
              "actionStatus": "CompletedActionStatus",
              "object": {
                "@type": "CreativeWork",
                "name": `${analytics.totalGenerated} SSH Keys Generated`,
                "description": `Session statistics: ${analytics.totalBatches} batches, ${analytics.securityScore}/100 security score`
              },
              "startTime": new Date(Date.now() - analytics.sessionTime * 1000).toISOString(),
              "endTime": new Date().toISOString()
            })}
          </script>
        )}
      </Helmet>
      
      <div className={`${isDarkMode ? 'dark' : ''}`}>
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 p-4 transition-colors duration-300">
          {/* Header */}
          <div className="max-w-7xl mx-auto mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 gap-4">
              <div className="flex items-center space-x-3">
                <Key className="w-8 h-8 text-green-600 dark:text-green-400" aria-hidden="true" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                    SSH Key Generator
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Generate secure SSH key pairs for authentication and server access
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 flex-wrap">
                {/* Security Level */}
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Security:</span>
                  <select
                    value={securityLevel}
                    onChange={(e) => setSecurityLevel(e.target.value as SecurityLevel)}
                    className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                    aria-label="Select security level for key generation"
                  >
                    <option value="standard">Standard</option>
                    <option value="high">High</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                  <div className={`w-2 h-2 rounded-full ${getSecurityLevelColor(securityLevel)}`} />
                </div>

                {/* View Mode */}
                <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-green-100 dark:bg-green-900' : 'bg-white dark:bg-gray-700'} transition-colors`}
                    title="Grid View"
                  >
                    <Grid className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-green-100 dark:bg-green-900' : 'bg-white dark:bg-gray-700'} transition-colors`}
                    title="List View"
                  >
                    <List className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  </button>
                </div>

                {/* Action Buttons */}
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Analytics"
                >
                  <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Settings"
                >
                  <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Toggle Theme"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
                
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                  {isFullscreen ? (
                    <Minimize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Maximize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className={`${showSettings || showAnalytics ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
              {/* Control Panel */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 mb-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={generateSSHKeyBatch}
                      disabled={isGenerating}
                      className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                      aria-label={`Generate ${settings.batchSize} SSH key pairs`}
                    >
                      {isGenerating ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <Zap className="w-5 h-5" />
                      )}
                      <span>{isGenerating ? 'Generating...' : `Generate ${settings.batchSize}`}</span>
                    </button>

                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Type:</span>
                      <select
                        value={settings.keyType}
                        onChange={(e) => setSettings(prev => ({ ...prev, keyType: e.target.value as any }))}
                        className="text-sm px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                      >
                        {Object.entries(keyTypeConfig).map(([key, config]) => (
                          <option key={key} value={key}>{config.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Size:</span>
                      <select
                        value={settings.keySize}
                        onChange={(e) => setSettings(prev => ({ ...prev, keySize: parseInt(e.target.value) }))}
                        className="text-sm px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                      >
                        {keyTypeConfig[settings.keyType].sizes.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Batch:</span>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => setSettings(prev => ({ ...prev, batchSize: Math.max(1, prev.batchSize - 1) }))}
                          className="p-1 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm font-medium text-gray-800 dark:text-white">
                          {settings.batchSize}
                        </span>
                        <button
                          onClick={() => setSettings(prev => ({ ...prev, batchSize: Math.min(10, prev.batchSize + 1) }))}
                          className="p-1 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowKeys(!showKeys)}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title={showKeys ? "Hide Keys" : "Show Keys"}
                    >
                      {showKeys ? (
                        <EyeOff className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      )}
                    </button>

                    <button
                      onClick={() => setShowPrivateKeys(!showPrivateKeys)}
                      className={`p-2 rounded transition-colors ${showPrivateKeys ? 'bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800' : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                      title={showPrivateKeys ? "Hide Private Keys" : "Show Private Keys"}
                    >
                      {showPrivateKeys ? (
                        <Unlock className="w-4 h-4 text-red-600 dark:text-red-400" />
                      ) : (
                        <Lock className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      )}
                    </button>

                    <button
                      onClick={exportKeys}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="Export Keys"
                    >
                      <Download className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    </button>

                    <button
                      onClick={clearAllKeys}
                      className="p-2 rounded bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                      title="Clear All"
                    >
                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>{sshKeys.length} SSH key pairs generated</span>
                  <span>Format: {settings.format.toUpperCase()}</span>
                </div>
              </div>

              {/* SSH Key Grid/List */}
              {sshKeys.length > 0 ? (
                <div className={`
                  ${viewMode === 'grid' 
                    ? 'grid grid-cols-1 lg:grid-cols-2 gap-4' 
                    : 'space-y-3'
                  }
                `}>
                  {sshKeys.map((key) => (
                    <div
                      key={key.id}
                      className={`
                        bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow border-l-4 border-green-500
                        ${viewMode === 'list' ? 'p-4' : 'p-4'}
                      `}
                    >
                      {viewMode === 'grid' ? (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span className={`text-xs font-medium px-2 py-1 rounded ${getKeyTypeColor(key.keyType)} bg-gray-100 dark:bg-gray-700`}>
                                {keyTypeConfig[key.keyType as keyof typeof keyTypeConfig]?.label} {key.keySize}
                              </span>
                              <span className={`text-xs font-medium px-2 py-1 rounded ${getStrengthColor(key.strength)}`}>
                                {key.strength.replace('_', ' ')}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              {key.hasPassphrase && <Lock className="w-3 h-3 text-green-500" />}
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {key.format.toUpperCase()}
                              </span>
                            </div>
                          </div>
                          
                          {showKeys && (
                            <div className="space-y-3">
                              {/* Public Key */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Public Key</label>
                                  <div className="flex space-x-1">
                                    <button
                                      onClick={() => copyKey(key.publicKey, key.id, 'public')}
                                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                      title="Copy Public Key"
                                    >
                                      {copiedId === `${key.id}-public` ? (
                                        <Check className="w-3 h-3 text-green-500" />
                                      ) : (
                                        <Copy className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                      )}
                                    </button>
                                    <button
                                      onClick={() => downloadKey(key, 'public')}
                                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                      title="Download Public Key"
                                    >
                                      <Download className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                    </button>
                                  </div>
                                </div>
                                <textarea
                                  value={key.publicKey}
                                  readOnly
                                  className="w-full p-2 text-xs font-mono bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded focus:outline-none resize-none"
                                  rows={2}
                                />
                              </div>
                              
                              {/* Private Key */}
                              {showPrivateKeys && (
                                <div>
                                  <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-medium text-red-700 dark:text-red-300">Private Key</label>
                                    <div className="flex space-x-1">
                                      <button
                                        onClick={() => copyKey(key.privateKey, key.id, 'private')}
                                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                        title="Copy Private Key"
                                      >
                                        {copiedId === `${key.id}-private` ? (
                                          <Check className="w-3 h-3 text-green-500" />
                                        ) : (
                                          <Copy className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                        )}
                                      </button>
                                      <button
                                        onClick={() => downloadKey(key, 'private')}
                                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                        title="Download Private Key"
                                      >
                                        <Download className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                      </button>
                                    </div>
                                  </div>
                                  <textarea
                                    value={key.privateKey}
                                    readOnly
                                    className="w-full p-2 text-xs font-mono bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-600 rounded focus:outline-none resize-none"
                                    rows={3}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Fingerprint and metadata */}
                          <div className="space-y-2 text-xs">
                            {key.fingerprint && (
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Fingerprint:</span>
                                <p className="font-mono text-gray-800 dark:text-white break-all">{key.fingerprint}</p>
                              </div>
                            )}
                            {key.comment && (
                              <div>
                                <span className="text-gray-600 dark:text-gray-400">Comment:</span>
                                <p className="text-gray-800 dark:text-white">{key.comment}</p>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex justify-center">
                            <button
                              onClick={() => downloadKey(key, 'both')}
                              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                            >
                              <Download className="w-4 h-4" />
                              <span>Download Key Pair</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className={`text-xs font-medium px-2 py-1 rounded ${getKeyTypeColor(key.keyType)} bg-gray-100 dark:bg-gray-700`}>
                                {keyTypeConfig[key.keyType as keyof typeof keyTypeConfig]?.label} {key.keySize}
                              </span>
                              <span className={`text-xs font-medium px-2 py-1 rounded ${getStrengthColor(key.strength)}`}>
                                {key.strength.replace('_', ' ')}
                              </span>
                              {key.hasPassphrase && <Lock className="w-3 h-3 text-green-500" />}
                            </div>
                            {showKeys && (
                              <div className="space-y-1">
                                <input
                                  type="text"
                                  value={key.publicKey}
                                  readOnly
                                  className="w-full p-2 text-xs font-mono bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded focus:outline-none"
                                />
                                {key.fingerprint && (
                                  <p className="text-xs font-mono text-gray-600 dark:text-gray-400 truncate">
                                    {key.fingerprint}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="ml-4 flex items-center space-x-2">
                            <button
                              onClick={() => copyKey(key.publicKey, key.id, 'public')}
                              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              {copiedId === `${key.id}-public` ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              )}
                            </button>
                            <button
                              onClick={() => downloadKey(key, 'both')}
                              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              <Download className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12 text-center">
                  <Server className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-2">
                    No SSH keys generated yet
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Generate secure SSH key pairs for server authentication and secure connections
                  </p>
                  <button
                    onClick={generateSSHKeyBatch}
                    className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200"
                  >
                    Generate SSH Keys
                  </button>
                </div>
              )}
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4 max-h-screen overflow-y-auto">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    SSH Key Settings
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Key Configuration */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Key Configuration</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Key Type</label>
                          <select
                            value={settings.keyType}
                            onChange={(e) => setSettings(prev => ({ ...prev, keyType: e.target.value as any }))}
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500"
                          >
                            {Object.entries(keyTypeConfig).map(([key, config]) => (
                              <option key={key} value={key}>{config.label} - {config.description}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Key Size</label>
                          <select
                            value={settings.keySize}
                            onChange={(e) => setSettings(prev => ({ ...prev, keySize: parseInt(e.target.value) }))}
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500"
                          >
                            {keyTypeConfig[settings.keyType].sizes.map(size => (
                              <option key={size} value={size}>{size} bits</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Output Format</label>
                          <select
                            value={settings.format}
                            onChange={(e) => setSettings(prev => ({ ...prev, format: e.target.value as any }))}
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500"
                          >
                            <option value="openssh">OpenSSH</option>
                            <option value="pem">PEM</option>
                            <option value="pkcs8">PKCS#8</option>
                            <option value="rfc4716">RFC4716</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Security Options */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Security Options</h4>
                      <div className="space-y-3">
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.usePassphrase}
                            onChange={(e) => setSettings(prev => ({ ...prev, usePassphrase: e.target.checked }))}
                            className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Use Passphrase</span>
                        </label>
                        
                        {settings.usePassphrase && (
                          <input
                            type="password"
                            value={settings.passphrase}
                            onChange={(e) => setSettings(prev => ({ ...prev, passphrase: e.target.value }))}
                            placeholder="Enter passphrase..."
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500"
                          />
                        )}
                        
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Cipher</label>
                          <select
                            value={settings.cipher}
                            onChange={(e) => setSettings(prev => ({ ...prev, cipher: e.target.value as any }))}
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500"
                          >
                            <option value="aes128-ctr">AES128-CTR</option>
                            <option value="aes192-ctr">AES192-CTR</option>
                            <option value="aes256-ctr">AES256-CTR</option>
                            <option value="aes128-gcm">AES128-GCM</option>
                            <option value="aes256-gcm">AES256-GCM</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Comment Options */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Comment Options</h4>
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={settings.comment}
                          onChange={(e) => setSettings(prev => ({ ...prev, comment: e.target.value }))}
                          placeholder="user@hostname or email"
                          className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500"
                        />
                        
                        {[
                          { key: 'emailInComment', label: 'Include Email', desc: 'Add email to comment' },
                          { key: 'timestampInComment', label: 'Include Timestamp', desc: 'Add generation time' }
                        ].map(({ key, label, desc }) => (
                          <label key={key} className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings[key as keyof SSHKeySettings] as boolean}
                              onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                            />
                            <div>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Advanced Options */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Advanced Options</h4>
                      {[
                        { key: 'generateFingerprint', label: 'Generate Fingerprint', desc: 'Calculate SHA256 fingerprint' },
                        { key: 'includePublicKey', label: 'Include Public Key', desc: 'Generate public key' },
                        { key: 'includePrivateKey', label: 'Include Private Key', desc: 'Generate private key' }
                      ].map(({ key, label, desc }) => (
                        <div key={key}>
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings[key as keyof SSHKeySettings] as boolean}
                              onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                            />
                            <div>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Panel */}
            {showAnalytics && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Analytics
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div className="text-xl font-bold text-green-600 dark:text-green-400">
                          {analytics.totalGenerated}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Keys Generated
                        </div>
                      </div>
                      <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {analytics.totalBatches}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Batches
                        </div>
                      </div>
                      <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                          {analytics.averageKeySize}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Key Size
                        </div>
                      </div>
                      <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                        <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                          {analytics.securityScore}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Security Score
                        </div>
                      </div>
                    </div>

                    {/* Key Type Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Key Types
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(analytics.keyTypeDistribution).map(([type, count]) => (
                          <div key={type} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600 dark:text-gray-400 uppercase">
                              {type}
                            </span>
                            <span className="font-medium text-gray-800 dark:text-white">
                              {count}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Strength Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Key Strength
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(analytics.strengthDistribution).map(([strength, count]) => (
                          <div key={strength} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600 dark:text-gray-400 capitalize">
                              {strength.replace('_', ' ')}
                            </span>
                            <span className="font-medium text-gray-800 dark:text-white">
                              {count}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Key Sizes */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Popular Sizes
                      </h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {Object.entries(analytics.keySizeDistribution)
                          .sort(([,a], [,b]) => b - a)
                          .slice(0, 5)
                          .map(([size, count]) => (
                          <div key={size} className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400">
                            <span>{size} bits</span>
                            <span>{count} times</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Security Info */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-green-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Security Level
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${getSecurityLevelColor(securityLevel)}`} />
                        <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                          {securityLevel}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {securityLevel === 'enterprise' 
                          ? 'Hardware-grade randomness' 
                          : securityLevel === 'high'
                          ? 'Enhanced cryptographic security'
                          : 'Standard secure generation'}
                      </p>
                    </div>

                    {/* Passphrase Usage */}
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600 dark:text-green-400">
                          {analytics.passphraseUsage}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Keys with Passphrase
                        </div>
                      </div>
                    </div>

                    {/* Session Time */}
                    <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                      Session: {Math.floor(analytics.sessionTime / 60)}m {analytics.sessionTime % 60}s
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SSHKeyGenerator;